import { Card, Typography, Stack, Chip, Divider, Box } from '@mui/material';
import { pluralize } from '../../../utils/pluralize';

interface Props {
  stats: any;
}

type ChipColor = 'success' | 'warning' | 'error';

interface StatusResult {
  label: string;
  color: ChipColor;
}

export default function BusinessInsights({ stats }: Props) {
  const totalIncome = stats?.totalIncome ?? 0;
  const totalExpenditure = stats?.totalExpenditure ?? 0;
  const netProfit = stats?.netProfit ?? 0;

  const totalTransactions = stats?.totalTransactions ?? 0;
  const reconciledTransactions = stats?.reconciledCount ?? 0;
  const unclassifiedTransactions = stats?.unclassifiedCount ?? 0;

  const profitMargin = totalIncome > 0 ? (netProfit / totalIncome) * 100 : 0;

  const expenseRatio =
    totalIncome > 0 ? (totalExpenditure / totalIncome) * 100 : 0;

  const reconciliationProgress =
    totalTransactions > 0
      ? (reconciledTransactions / totalTransactions) * 100
      : 0;

  const getProfitabilityStatus = (): StatusResult => {
    if (profitMargin >= 20) return { label: 'Strong Profit', color: 'success' };

    if (profitMargin > 0) return { label: 'Low Profit', color: 'warning' };

    return { label: 'Loss', color: 'error' };
  };

  const getExpenseStatus = (): StatusResult => {
    if (expenseRatio <= 60) return { label: 'Controlled', color: 'success' };

    if (expenseRatio <= 85) return { label: 'Increasing', color: 'warning' };

    return { label: 'High Expenses', color: 'error' };
  };

  const getReconciliationStatus = (): StatusResult => {
    if (reconciliationProgress >= 90)
      return { label: 'Excellent', color: 'success' };

    if (reconciliationProgress >= 60)
      return { label: 'In Progress', color: 'warning' };

    return { label: 'Needs Attention', color: 'error' };
  };

  const getClassificationStatus = (): StatusResult => {
    if (unclassifiedTransactions === 0)
      return { label: 'Complete', color: 'success' };

    if (unclassifiedTransactions <= 10)
      return { label: 'Minor Pending', color: 'warning' };

    return { label: 'Large Backlog', color: 'error' };
  };

  const profitability = getProfitabilityStatus();
  const expenses = getExpenseStatus();
  const reconciliation = getReconciliationStatus();
  const classification = getClassificationStatus();

  const insights: string[] = [];

  if (profitMargin >= 20) {
    insights.push(
      `Profit margin is ${profitMargin.toFixed(
        1,
      )}%, indicating strong profitability.`,
    );
  } else if (profitMargin > 0) {
    insights.push(
      `Profit margin is ${profitMargin.toFixed(
        1,
      )}%. Profitability should be improved.`,
    );
  } else {
    insights.push(
      'Business is operating at a loss. Review major expense categories.',
    );
  }

  if (expenseRatio > 85) {
    insights.push(
      `Expenses consume ${expenseRatio.toFixed(
        1,
      )}% of income, which is higher than recommended.`,
    );
  }

  if (reconciliationProgress < 80) {
    insights.push(
      `Only ${reconciliationProgress.toFixed(
        0,
      )}% of transactions have been reconciled.`,
    );
  }

  if (unclassifiedTransactions > 0) {
    insights.push(
      `${pluralize(unclassifiedTransactions, 'transaction')} still ${unclassifiedTransactions === 1 ? 'requires' : 'require'} categorisation.`,
    );
  }

  if (stats?.vatRecoverable > 0) {
    insights.push(
      `Recoverable VAT available: ${stats.vatRecoverable.toFixed(2)}.`,
    );
  }

  return (
    <Card sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Business Insights
      </Typography>

      <Stack spacing={1.5}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body2" color="text.secondary">
            Profitability
          </Typography>

          <Chip
            size="small"
            label={profitability.label}
            color={profitability.color}
          />
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body2" color="text.secondary">
            Expense Control
          </Typography>

          <Chip size="small" label={expenses.label} color={expenses.color} />
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body2" color="text.secondary">
            Reconciliation
          </Typography>

          <Chip
            size="small"
            label={reconciliation.label}
            color={reconciliation.color}
          />
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body2" color="text.secondary">
            Classification
          </Typography>

          <Chip
            size="small"
            label={classification.label}
            color={classification.color}
          />
        </Stack>
      </Stack>

      <Divider sx={{ my: 2 }} />

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Management Insights
        </Typography>

        <Stack spacing={0.75}>
          {insights.map((item, index) => (
            <Typography key={index} variant="body2" color="text.secondary">
              • {item}
            </Typography>
          ))}
        </Stack>
      </Box>
    </Card>
  );
}
